import { ChangeDetectionStrategy, Component, type DoCheck, inject } from '@angular/core';
import { FieldType, type FormlyFieldConfig } from '@ngx-formly/core';

import { DETAILS_SERVICE_TOKEN } from '../../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { OverviewCardComponent } from './overview-card.component';
import { ReadSectionEmptyComponent } from './read-section-empty.component';
import { readonlyPresenceValue } from './readonly.types';
import { isOverviewVisible, overviewAncestorsVisible } from './overview.types';
import type { OverviewRow, OverviewSection } from './overview.types';

/** Jump link on a card head; the chevron is part of the label, matching the design language. */
const SEE_ALL_LABEL = 'Bekijk alles ›';

/**
 * The synthetic Overzicht tab of the read view (#21239, task 21250).
 *
 * Never configured by an admin and never offered in the form-builder type picker: `DetailsService`
 * builds this node itself when the module opted in, and registers it in `INTERNAL_INPUTS`
 * (`formly-fields/index.ts`). It renders one `dd-overview-card` per section, each holding the
 * flagged answers of one tab/page.
 *
 * A row's VALUE is read here, at render time, not baked into the section: the section carries the
 * very field objects Formly is building, and their `formControl` only exists once that build has
 * run. Reading it in `DetailsService.fields()` — which runs before — would print `undefined` for
 * every answer.
 */
@Component({
  selector: 'formly-field-overview-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverviewCardComponent, ReadSectionEmptyComponent],
  template: `
    <!-- The same cards in every mode (decision 2026-09-16). They used to be recessed and inert on
         the edit face, under a banner saying Overzicht was read-view-only — but a summary of what
         you are editing is worth reading, and its "Bekijk alles" links are worth following. What
         the edit face needs is only that it does not OPEN here, which is what the node's
         skipInitialSelection flag says (see addOverviewTab in details.service.ts). -->
    <div class="flex flex-col gap-4">
      @for (section of visibleSections(); track section) {
        <dd-overview-card
          [label]="section.label"
          [sectionKey]="section.key"
          [rows]="rowsOf(section)"
          [seeAllLabel]="seeAllLabelFor(section)"
          (seeAll)="goToSection(section)" />
      } @empty {
        <!-- Every flagged answer role-hidden, or (the common case) every flagged optional answer
             empty and hidden by readonlyExtension: the tab must not land the reader on a blank
             page. Same note as any other empty read section. -->
        <dd-read-section-empty />
      }
    </div>
  `,
})
export class OverviewTabComponent extends FieldType implements DoCheck {
  /**
   * Optional, like every other field that talks to the detail page: this type also renders in
   * Storybook and in the admin form preview, neither of which provides the service.
   */
  readonly #detailsService = inject(DETAILS_SERVICE_TOKEN, { optional: true });

  /**
   * Per-check memo for {@link rowsOf} (review finding): the template calls `rowsOf(section)`
   * twice per section on every check — once from `visibleSections()`'s length filter, once from
   * the `[rows]` binding — which re-filtered and re-mapped the same section twice. Cleared in
   * {@link ngDoCheck}, which Angular runs once per check regardless of OnPush, so the cache never
   * outlives the check it was built for and a live value written between two checks (Formly
   * expressions, `readonlyPresenceValue` reading the live control or an upload's entry documents)
   * still surfaces on the next one — this only removes the DUPLICATE work within a single check,
   * not any recomputation.
   */
  readonly #rowsCache = new Map<OverviewSection, OverviewRow[]>();

  ngDoCheck(): void {
    this.#rowsCache.clear();
  }

  sections(): OverviewSection[] {
    return (this.props['sections'] as OverviewSection[]) ?? [];
  }

  /**
   * The sections that this reader may actually see, with at least one row left to show.
   *
   * Both filters run HERE rather than in `buildOverviewSections`, because both of the things they
   * read (`hide`, `className`) are written by Formly expressions that have not run yet when the
   * sections are built — see {@link isOverviewVisible}. A tab hidden for this role would otherwise
   * be summarised on a card the role is not allowed to open, and a tab whose every flagged answer
   * is hidden would render as an empty card.
   */
  visibleSections(): OverviewSection[] {
    return this.sections().filter(section => isOverviewVisible(section.source) && this.rowsOf(section).length > 0);
  }

  /**
   * A method, not a computed signal: `props`/`hide` are plain objects Formly mutates in place, so
   * a signal would never see the write. Formly marks the field view for check after each
   * expression pass, which is what re-runs this.
   *
   * Fields the read view hid are skipped for the same reason they were hidden on their own tab
   * (task 21245): an empty OPTIONAL answer is noise. A required-but-empty one keeps its `hide`
   * false and reaches the row, where it shows the "Ontbreekt" badge. A field the role may not see
   * at all (`className: '… hidden'`) is skipped for a harder reason: repeating it here would be a
   * leak.
   *
   * The same two questions are asked of every GROUP the answer sits in, up to this section
   * (`overviewAncestorsVisible`): a hidden block does not mark the fields inside it as hidden, so
   * without that walk its answers reappear here one card over.
   */
  rowsOf(section: OverviewSection): OverviewRow[] {
    const cached = this.#rowsCache.get(section);
    if (cached) {
      return cached;
    }
    // The same read row `ReadonlyWrapper` mounts on the field's own tab, so it must read the
    // same presence value — an `upload` field keeps its files on the entry, not in its control
    // (task 21243, defect C1).
    const rows = section.fields
      .filter(field => isOverviewVisible(field) && overviewAncestorsVisible(field, section.source))
      .map(field => ({ field, value: readonlyPresenceValue(field) }));
    this.#rowsCache.set(section, rows);
    return rows;
  }

  /**
   * The link is only offered where it can actually do something.
   *
   * `switchTab$` is the detail page's ONE inbound channel for section switching, and both
   * containers listen to it: `tabs.component.ts` has always done so, `pages.component.ts` since
   * fix round 1 of this task. The fallback section of a form with neither container has nowhere
   * to jump to and keeps its link suppressed.
   */
  seeAllLabelFor(section: OverviewSection): string | null {
    return section.key && this.#canSwitchSections() ? SEE_ALL_LABEL : null;
  }

  goToSection(section: OverviewSection): void {
    if (this.seeAllLabelFor(section)) {
      this.#detailsService!.switchTab$.next(section.key);
    }
  }

  /** The container this synthetic node was prepended into — `tabs`, `pages`, or nothing. */
  #canSwitchSections(): boolean {
    const parent = this.field.parent as FormlyFieldConfig | undefined;
    return !!this.#detailsService && (parent?.type === 'tabs' || parent?.type === 'pages');
  }
}
